import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GoogleButton } from "@/components/GoogleButton";
import {
    User, Mail, Lock, Eye, EyeOff, Loader2, ShieldCheck, BadgeCheck, CreditCard,
    ShoppingBag, Store, Repeat, Gavel, Package, BarChart3, Headphones, UserPlus,
    ArrowRight, Sparkles,
} from "lucide-react";

const img = (id, w = 600) => `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const HERO_FEATURES = [
    { icon: BadgeCheck, title: "Verified members only.", text: "Email + phone verification keeps it clean." },
    { icon: ShieldCheck, title: "Sealed-bid fairness.", text: "One private bid. No sniping. No games." },
    { icon: CreditCard, title: "Secure payments & payouts.", text: "Powered by Stripe." },
];

const TRUST = [
    { icon: UserPlus, title: "Free to Join", text: "Create your account in under a minute" },
    { icon: BadgeCheck, title: "Verified Community", text: "Every member is email & phone verified" },
    { icon: Lock, title: "Private by Design", text: "Your bids stay sealed and confidential" },
    { icon: Headphones, title: "Here to Help", text: "Real support whenever you need it" },
];

const PERKS = [
    { icon: Gavel, title: "Bid with Confidence", text: "Submit your best bid and let the market decide." },
    { icon: Package, title: "Sell with Ease", text: "List your items quickly and reach serious buyers." },
    { icon: BarChart3, title: "Track Everything", text: "Monitor your bids, wins, and sales in one place." },
    { icon: ShieldCheck, title: "Built on Fairness", text: "No sniping. No tricks. Just a level playing field." },
];

const CTA_IMAGES = [
    { id: "1523275335684-37898b6baf30", alt: "Luxury watch" },
    { id: "1516035069371-29a1b244cc32", alt: "Mirrorless camera" },
    { id: "1496181133206-80ce9b88a853", alt: "Laptop" },
];

const ROLES = [
    { val: "buyer", label: "Buy", icon: ShoppingBag },
    { val: "seller", label: "Sell", icon: Store },
    { val: "both", label: "Both", icon: Repeat },
];

export default function Signup() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = await signup(form);
            toast.success("Account created — let's verify it");
            if (data.email_sent) {
                navigate("/account/email-sent", { state: { email: form.email } });
            } else {
                if (data.dev_email_token) {
                    toast.message("Dev email code", { description: `Your verification code: ${data.dev_email_token}` });
                }
                navigate("/account/verify");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="signup-page">
            {/* ---------------- HERO ---------------- */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_55%_at_50%_0%,rgba(214,158,46,0.12)_0%,rgba(214,158,46,0)_55%)]" />
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(50%_50%_at_85%_30%,rgba(33,140,255,0.12)_0%,rgba(33,140,255,0)_60%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20">
                    {/* Left: welcome copy */}
                    <div className="order-2 lg:order-1">
                        <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
                            Create Your Account.
                            <span className="mt-1 block bg-gradient-to-r from-brass to-[hsl(38_92%_62%)] bg-clip-text text-transparent">
                                Join the Fair Market.
                            </span>
                        </h1>
                        <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                            Set up your OmniAuc account to bid on sealed auctions or sell to serious buyers — verified, fair, and secure.
                        </p>
                        <ul className="mt-8 space-y-5">
                            {HERO_FEATURES.map(({ icon: Icon, title, text }) => (
                                <li key={title} className="flex items-start gap-4">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brass/10 text-brass ring-1 ring-brass/20">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <p className="text-sm leading-relaxed">
                                        <span className="font-semibold text-foreground">{title}</span>{" "}
                                        <span className="text-muted-foreground">{text}</span>
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right: signup card */}
                    <div className="order-1 mx-auto w-full max-w-md lg:order-2">
                        <Card className="relative overflow-hidden p-6 shadow-2xl sm:p-8">
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brass/50 to-transparent" />
                            <div className="text-center">
                                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brass/10 text-brass ring-1 ring-brass/20">
                                    <Sparkles className="h-6 w-6" />
                                </span>
                                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">Create your account</h2>
                                <p className="mt-1 text-sm text-muted-foreground">Bid, sell, and win — fairly</p>
                            </div>

                            <form onSubmit={submit} className="mt-7 space-y-4">
                                <div className="relative">
                                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        data-testid="signup-name" autoComplete="name"
                                        value={form.name} onChange={(e) => set("name", e.target.value)} required
                                        placeholder="Full name" className="pl-10" aria-label="Full name"
                                    />
                                </div>
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        data-testid="signup-email" type="email" autoComplete="email"
                                        value={form.email} onChange={(e) => set("email", e.target.value)} required
                                        placeholder="Email address" className="pl-10" aria-label="Email address"
                                    />
                                </div>
                                <div>
                                    <div className="relative">
                                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            data-testid="signup-password" type={showPw ? "text" : "password"} autoComplete="new-password" minLength={6}
                                            value={form.password} onChange={(e) => set("password", e.target.value)} required
                                            placeholder="Password" className="px-10" aria-label="Password"
                                        />
                                        <button
                                            type="button" onClick={() => setShowPw((v) => !v)} data-testid="signup-toggle-password"
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                                        >
                                            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <p className="mt-1.5 pl-1 text-xs text-muted-foreground">At least 6 characters.</p>
                                </div>

                                <div>
                                    <p className="mb-2 text-sm font-medium">I want to</p>
                                    <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-3 gap-2.5">
                                        {ROLES.map(({ val, label, icon: Icon }) => (
                                            <Label
                                                key={val} htmlFor={`role-${val}`}
                                                className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border p-3 text-sm transition-colors ${form.role === val ? "border-brass bg-brass/10 text-foreground ring-1 ring-brass/30" : "border-border text-muted-foreground hover:border-brass/40"}`}
                                            >
                                                <RadioGroupItem id={`role-${val}`} value={val} className="sr-only" />
                                                <Icon className={`h-5 w-5 ${form.role === val ? "text-brass" : ""}`} />
                                                {label}
                                            </Label>
                                        ))}
                                    </RadioGroup>
                                </div>

                                <Button type="submit" size="lg" className="w-full rounded-[10px] text-base font-semibold" disabled={loading} data-testid="signup-submit">
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
                                </Button>
                            </form>

                            {/* Google appears automatically when OAuth is configured */}
                            <GoogleButton />

                            <p className="mt-6 text-center text-sm text-muted-foreground">
                                Already have an account?{" "}
                                <Link to="/login" data-testid="signup-login-link" className="font-semibold text-brass hover:underline">Log in</Link>
                            </p>
                            <p className="mt-3 text-center text-xs text-muted-foreground">
                                By creating an account you agree to our{" "}
                                <Link to="/legal/terms" className="underline hover:text-foreground">Terms</Link> and{" "}
                                <Link to="/legal/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* ---------------- TRUST STRIP ---------------- */}
            <section className="border-b border-border bg-[hsl(221_59%_7%)]">
                <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 sm:grid-cols-2 lg:grid-cols-4">
                    {TRUST.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="flex items-start gap-4" data-testid="signup-trust-item">
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-card text-electric">
                                <Icon className="h-5 w-5" />
                            </span>
                            <div>
                                <h3 className="font-display text-sm font-bold text-brass">{title}</h3>
                                <p className="mt-0.5 text-sm text-muted-foreground">{text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------------- EVERYTHING YOU NEED ---------------- */}
            <section className="bg-background">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            Everything You Need to <span className="text-brass">Win.</span>
                        </h2>
                        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-brass" />
                        <p className="mt-5 text-base text-muted-foreground">
                            One account unlocks bidding, selling, tracking, and a community built on fairness.
                        </p>
                    </div>

                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {PERKS.map(({ icon: Icon, title, text }) => (
                            <div key={title} className="rounded-2xl border border-border bg-card p-6 text-center transition-colors hover:border-brass/40" data-testid="signup-perk-card">
                                <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brass/20 to-electric/10 ring-1 ring-brass/30">
                                    <Icon className="h-7 w-7 text-brass" />
                                    <div className="absolute inset-0 -z-10 rounded-2xl bg-brass/25 blur-xl" />
                                </div>
                                <h3 className="mt-5 font-display text-base font-bold">{title}</h3>
                                <p className="mx-auto mt-2 max-w-[14rem] text-sm leading-relaxed text-muted-foreground">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- READY CTA ---------------- */}
            <section className="relative overflow-hidden border-t border-border bg-[hsl(221_59%_7%)]">
                <div className="absolute inset-y-0 right-0 -z-10 w-2/3 bg-[radial-gradient(60%_120%_at_100%_50%,rgba(214,158,46,0.16)_0%,rgba(214,158,46,0)_60%)]" />
                <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4 py-12 sm:px-6 lg:flex-row lg:justify-between">
                    <div className="text-center lg:text-left">
                        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">See what's up for auction.</h2>
                        <p className="mt-2 text-muted-foreground">Take a look around — your next great find could be live right now.</p>
                        <Button asChild size="lg" className="mt-6 rounded-[10px]" data-testid="signup-browse-cta">
                            <Link to="/auctions">Browse auctions <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        {CTA_IMAGES.map(({ id, alt }) => (
                            <div key={id} className="h-24 w-24 overflow-hidden rounded-xl border border-border sm:h-28 sm:w-28">
                                <img src={img(id, 300)} alt={alt} loading="lazy" className="h-full w-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
